// App-local locale configuration for the Studio Desktop marketing pages.
//
// Unlike every other cf-web app, this app ships far more languages than the
// shared `cf-i18n` allowlist. `cf-i18n` is pinned to the marketplace's GraphQL
// `Language` enum (8 locales) and is imported by the other apps, so widening it
// would force 50 locales onto everyone. Landing-page copy here is static
// marketing content with no GraphQL dependency, so the supported-locale list
// lives with the app that owns it.
//
// `[code, native name]` pairs, English first. Order drives the language switcher
// list, `generateStaticParams`, and the sitemap/hreflang alternates.
export const LOCALES = [
  ['en', 'English'],
  ['es', 'Español'],
  ['pt', 'Português (BR)'],
  ['fr', 'Français'],
  ['de', 'Deutsch'],
  ['ja', '日本語'],
  ['ko', '한국어'],
  ['zh', '简体中文'],
  ['zh-TW', '繁體中文'],
  ['ru', 'Русский'],
  ['it', 'Italiano'],
  ['nl', 'Nederlands'],
  ['pl', 'Polski'],
  ['tr', 'Türkçe'],
  ['id', 'Bahasa Indonesia'],
  ['vi', 'Tiếng Việt'],
  ['th', 'ไทย'],
  ['hi', 'हिन्दी'],
  ['uk', 'Українська'],
  ['bn', 'বাংলা'],
  ['mr', 'मराठी'],
  ['ta', 'தமிழ்'],
  ['te', 'తెలుగు'],
  ['gu', 'ગુજરાતી'],
  ['kn', 'ಕನ್ನಡ'],
  ['ml', 'മലയാളം'],
  ['pa', 'ਪੰਜਾਬੀ'],
  ['ro', 'Română'],
  ['cs', 'Čeština'],
  ['hu', 'Magyar'],
  ['el', 'Ελληνικά'],
  ['sv', 'Svenska'],
  ['no', 'Norsk'],
  ['da', 'Dansk'],
  ['fi', 'Suomi'],
  ['sk', 'Slovenčina'],
  ['bg', 'Български'],
  ['sr', 'Српски'],
  ['hr', 'Hrvatski'],
  ['lt', 'Lietuvių'],
  ['lv', 'Latviešu'],
  ['et', 'Eesti'],
  ['ms', 'Bahasa Melayu'],
  ['fil', 'Filipino'],
  ['sw', 'Kiswahili'],
  ['kk', 'Қазақша'],
  ['uz', 'Oʻzbekcha'],
  ['az', 'Azərbaycan'],
  ['ka', 'ქართული'],
  ['mn', 'Монгол'],
] as const satisfies ReadonlyArray<readonly [string, string]>

export type Locale = (typeof LOCALES)[number][0]

/** Supported locale codes, English first. Used by routing + static params. */
export const SUPPORTED_LOCALES: Locale[] = LOCALES.map(([code]) => code)

export const DEFAULT_LOCALE: Locale = 'en'

/** `code → native name`, in display order, for the language switcher. */
export const localeLabels = new Map<Locale, string>(LOCALES.map(([code, label]) => [code, label]))

const SUPPORTED_LOCALE_SET = new Set<string>(SUPPORTED_LOCALES)

/** Narrows an unknown value to a supported locale code. */
export function isSupportedLocale(locale: unknown): locale is Locale {
  return typeof locale === 'string' && SUPPORTED_LOCALE_SET.has(locale)
}
